import { useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { STAGE_DEFINITIONS, type Dashboard } from '@tailoring-crm/shared';
import { DashboardAnalytics } from '../components/dashboard/dashboard-analytics.js';
import { DashboardTiles } from '../components/dashboard/dashboard-tiles.js';
import { TONE_CLASSES, staffTone } from '../components/dashboard/visuals.js';
import { PageHeader } from '../components/layout/page-header.js';
import { GuidedTour } from '../components/onboarding/guided-tour.js';
import { SetupProgress } from '../components/onboarding/setup-progress.js';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ListSkeleton,
  cn,
} from '@tailoring-crm/ui';
import { LoadError } from '../components/feedback/load-error.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { formatTime } from '../lib/format.js';
import { useOnboarding } from '../lib/onboarding/use-onboarding.js';
import { reportKeys, useDashboard, useRefreshDashboard } from '../lib/reports/use-reports.js';

/**
 * The shop's home screen, arranged around the questions somebody actually asks at nine in the
 * morning: what is due, what is late, what is finished, what is owed, and who is working on what.
 *
 * The order on the page is the order of the morning. Tiles first, because they are the whole
 * screen for most visits. Then what needs attention — overdue garments and anything a karigar has
 * flagged — because that is the list that changes what the day looks like. Then the analytics
 * grid (`DashboardAnalytics`): work in hand by stage for everybody, and the sales figures and
 * charts for whoever may see revenue. Then who is on what, which is what somebody reads when they
 * are deciding what to hand out.
 *
 * The figures say when they were computed. The API caches them for a minute (see the reports
 * module), so rather than pretending they are live, the page prints the time and offers a
 * refresh. A number that is quietly a minute old is a number somebody eventually catches being
 * wrong; a number that says how old it is never is.
 *
 * Setup and the guided tour stay at the top for a shop that has not finished either, and take
 * themselves away when it has — the difference between a prompt and a nag.
 */
export function DashboardPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isOwner = role === 'owner';
  // The commercial and configuration side of the shop is the owner's, and so is the checklist.
  const onboarding = useOnboarding({ enabled: isOwner });
  const progress = onboarding.data;

  // A karigar has their own screen; the API refuses them this one, so it is never asked for.
  const dashboard = useDashboard({ enabled: role !== 'tailor' });
  const refresh = useRefreshDashboard();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('dashboard.title')} description={t('dashboard.description')} />

      {progress && !progress.summary.finished ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('onboarding.checklistTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <SetupProgress progress={progress} />
            <div>
              <Button asChild variant="secondary">
                <Link to="/setup">{t('onboarding.continue')}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {progress ? <GuidedTour progress={progress} /> : null}

      {dashboard.isPending ? <ListSkeleton rows={4} /> : null}

      {dashboard.isError ? <LoadError onRetry={() => void dashboard.refetch()} /> : null}

      {dashboard.data ? (
        <>
          <DashboardTiles dashboard={dashboard.data} />

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span role="status">
              {t('dashboard.asOf', { time: formatTime(dashboard.data.computedAt) })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={refreshing}
              onClick={() => {
                setRefreshing(true);
                // The sales panels are a separate query; refresh means every figure on Home.
                void queryClient.invalidateQueries({ queryKey: [...reportKeys.all, 'sales'] });
                void refresh().finally(() => setRefreshing(false));
              }}
            >
              <RefreshCw aria-hidden="true" />
              {t('dashboard.refresh')}
            </Button>
          </div>

          <Attention dashboard={dashboard.data} />
          <DashboardAnalytics dashboard={dashboard.data} />
          <StaffLoad dashboard={dashboard.data} />
        </>
      ) : null}
    </div>
  );
}

// --- What is wrong ---------------------------------------------------------------------------

/**
 * How many orders the list shows before handing over to the orders screen. Enough to name the
 * worst of the morning; few enough that the workload below still makes the first screen.
 */
const ATTENTION_VISIBLE = 6;

interface AttentionGroup {
  key: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  reason: Dashboard['attention'][number]['reason'];
  /** Garment labels with how many of each, in the order the API listed them. */
  garments: Map<string, number>;
  stages: Set<Dashboard['attention'][number]['stage']>;
  daysLate: number;
  detail: string | null;
}

/**
 * The API lists garments; the shop thinks in orders. Three late pieces on one booking are one
 * customer to ring, not three rows that read as the same row twice. Grouped by order and reason,
 * in the order the API sent them — oldest promise first, then the flags.
 */
function groupAttention(rows: Dashboard['attention']): AttentionGroup[] {
  const groups = new Map<string, AttentionGroup>();
  for (const row of rows) {
    const key = `${row.orderId}-${row.reason}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        orderId: row.orderId,
        orderNumber: row.orderNumber,
        customerName: row.customerName,
        reason: row.reason,
        garments: new Map(),
        stages: new Set(),
        daysLate: 0,
        detail: row.detail,
      };
      groups.set(key, group);
    }
    group.garments.set(row.garmentTypeLabel, (group.garments.get(row.garmentTypeLabel) ?? 0) + 1);
    group.stages.add(row.stage);
    group.daysLate = Math.max(group.daysLate, row.daysLate);
    group.detail ??= row.detail;
  }
  return [...groups.values()];
}

function Attention({ dashboard }: { dashboard: Dashboard }): ReactElement | null {
  const { t } = useTranslation();
  if (dashboard.attention.length === 0) return null;

  const groups = groupAttention(dashboard.attention);
  const visible = groups.slice(0, ATTENTION_VISIBLE);
  const overdue = dashboard.counts.overdue;

  return (
    <section className="flex flex-col gap-2" aria-label={t('dashboard.attention')}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle aria-hidden="true" className="size-4 text-destructive" />
          {t('dashboard.attention')}
        </h2>
        {overdue > 0 ? (
          <Link
            to="/orders?due=overdue"
            className="text-xs font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('dashboard.viewAllOverdue', { count: overdue })}
          </Link>
        ) : null}
      </div>
      <ul className="flex flex-col gap-1.5">
        {visible.map((group) => {
          const stage = group.stages.size === 1 ? [...group.stages][0] : undefined;
          return (
            <li key={group.key}>
              <Link
                to={`/orders/${group.orderId}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3 text-sm hover:bg-accent"
              >
                <span className="min-w-0">
                  <span className="block font-medium">
                    {group.customerName} ·{' '}
                    {[...group.garments]
                      .map(([label, count]) => (count > 1 ? `${label} ×${count}` : label))
                      .join(', ')}
                  </span>
                  <span className="block text-muted-foreground">
                    {group.orderNumber}
                    {stage
                      ? ` · ${t(`stage.${stage}`, {
                          defaultValue: STAGE_DEFINITIONS[stage].defaultLabel,
                        })}`
                      : ''}
                  </span>
                </span>
                <span className="text-xs font-medium text-destructive">
                  {group.reason === 'overdue'
                    ? t('dashboard.daysLate', { count: group.daysLate })
                    : (group.detail ?? t('dashboard.flagged'))}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// --- Who is doing it ---------------------------------------------------------------------------

/** A bar's fill as a share of the biggest value in its list, so the busiest row reads full. */
function barWidth(value: number, most: number): string {
  return `${most === 0 ? 0 : (value / most) * 100}%`;
}

/**
 * The letters on a person's avatar: the first letter of each of the first two words of their
 * name. Taken by code point rather than by UTF-16 unit, so a name outside the Basic Multilingual
 * Plane never leaves half a character on the avatar.
 */
function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? '')
    .join('')
    .toUpperCase();
}

function StaffLoad({ dashboard }: { dashboard: Dashboard }): ReactElement | null {
  const { t } = useTranslation();
  if (dashboard.staff.length === 0) return null;
  const busiest = dashboard.staff.reduce((most, row) => Math.max(most, row.activeItems), 0);

  return (
    <section className="flex flex-col gap-2" aria-label={t('dashboard.whoIsOnWhat')}>
      <h2 className="text-sm font-semibold">{t('dashboard.whoIsOnWhat')}</h2>
      <ul className="flex flex-col gap-1.5">
        {dashboard.staff.map((row) => {
          // Hashed from the ID, so a person keeps their colour however the list is sorted.
          const toneClasses = TONE_CLASSES[staffTone(row.staffUserId)];
          return (
            <li
              key={row.staffUserId}
              className="flex items-center gap-3 rounded-lg border border-border p-2.5 text-sm"
            >
              <span
                aria-hidden="true"
                data-testid="staff-avatar"
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  toneClasses.chip,
                )}
              >
                {initialsOf(row.staffName)}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate font-medium">{row.staffName}</span>
                  <span className="flex shrink-0 items-center gap-2 text-xs">
                    {row.dueToday > 0 ? (
                      <span className="rounded-full bg-tone-amber/15 px-2 py-0.5 font-medium text-tone-amber">
                        {t('dashboard.dueTodayCount', { count: row.dueToday })}
                      </span>
                    ) : null}
                    <span className="text-muted-foreground">
                      {t('dashboard.activeItems', { count: row.activeItems })}
                    </span>
                  </span>
                </span>
                <span aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <span
                    className={cn('block h-full rounded-full', toneClasses.bar)}
                    style={{ width: barWidth(row.activeItems, busiest) }}
                  />
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default DashboardPage;
